<template>
  <div class="custom-field-input-container">
    <!-- Custom Field Selector (like country code) -->
    <div 
      class="custom-field-selector"
      :class="{ disabled: isDisabled }"
      @click="!isDisabled && openCustomFieldModal()"
      :title="getCustomFieldName()"
    >
      <div class="custom-field-name-text">{{ getCustomFieldName() }}</div>
      <VIcon :name="chevronDownOutline" class="icon-inner icon-gap"></VIcon>
    </div>
    <!-- Custom Field Value Input (like mobile number) -->
    <VTextField 
      :modelValue="getCustomFieldInputValue()"
      @input="(e) => onCustomFieldInputChange(typeof e === 'string' ? e : e?.target?.value || '')"
      :placeholder="placeholder"
      class="custom-field-value-input"
      :readonly="isDisabled"
      :maxlength="maxLength"
      appearance="ghost"
    />
    
    <!-- Custom Data Field Picker Modal -->
    <CustomDataFieldPickerModal 
      :canAddNew="false"
      v-model:isOpen="modalOpen" 
      @selected="onCustomFieldSelected" 
    />
  </div>
</template>

<script>
import CustomDataFieldPickerModal from './CustomDataFieldPickerModal.vue';

export default {
  name: 'CustomFieldInputKeyValue',
  components: {
    CustomDataFieldPickerModal
  },
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: String,
      default: ':'
    },
    placeholder: {
      type: String,
      default: 'Enter value'
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    maxLength: {
      type: [String, Number],
      default: 240
    }
  },
  data() {
    return {
      modalOpen: false,
      chevronDownOutline: 'chevron-down-line'
    };
  },
  methods: {
    openCustomFieldModal() {
      this.modalOpen = true;
    },
    
    onCustomFieldSelected(selectedValue) {
      // Remove angle brackets from selected value
      let cleanFieldName = selectedValue.replace(/[<>]/g, '');
      
      // Remove custom_field_ prefix if present
      if (cleanFieldName.startsWith('custom_field_')) {
        cleanFieldName = cleanFieldName.replace('custom_field_', '');
      }
      
      // Get current input value (after colon)
      const inputValue = this.getCustomFieldInputValue();
      
      // Emit combined value in format: "fieldName:inputValue"
      this.$emit('update:modelValue', `${cleanFieldName}:${inputValue}`);
      
      this.modalOpen = false;
    },
    
    onCustomFieldInputChange(inputValue) {
      // Get the field name part (before colon)
      const fieldName = this.getCustomFieldName();
      const cleanFieldName = fieldName === 'Select Custom Field' ? '' : fieldName;
      
      // Emit combined value in format: "fieldName:inputValue"
      this.$emit('update:modelValue', `${cleanFieldName}:${inputValue}`);
    },
    
    getCustomFieldName() {
      if (!this.modelValue) return 'Select Custom Field';
      const valueParts = this.modelValue.split(':');
      let fieldName = valueParts[0] || '';
      
      // Remove custom_field_ prefix if present for display
      if (fieldName.startsWith('custom_field_')) {
        fieldName = fieldName.replace('custom_field_', '');
      }
      
      return fieldName || 'Select Custom Field';
    },
    
    getCustomFieldInputValue() {
      if (!this.modelValue) return '';
      const valueParts = this.modelValue.split(':');
      return valueParts.length > 1 ? valueParts[1] : '';
    }
  }
};
</script>

<style scoped>
.custom-field-input-container {
  display: flex;
  align-items: center;
  border: 1px solid var(--gray-01);
  border-radius: 4px;
  overflow: hidden;
  background: var(--background);
  width: 100%;
}

.custom-field-selector {
  background: var(--gray-02);
  padding: 12px 16px;
  min-height: 44px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: 80px;
  max-width: 200px;
  justify-content: center;
  overflow: hidden;
  text-overflow: ellipsis;
}
.custom-field-name-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 130px;
}

.custom-field-selector:hover:not(.disabled) {
  color: var(--ion-color-primary);
}

.custom-field-selector.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: var(--gray-03);
}

.icon-gap {
  margin-left: 8px;
}

.custom-field-value-input {
  flex: 1;
}
</style>